import React, { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

/**
 * SplitPane Component — Two-panel layout with optional resizing
 *
 * A layout primitive for side-by-side content with an optional draggable divider.
 * Perfect for list/detail views, dashboards with inspectors, or any
 * main/aside layout.
 *
 * Usage:
 * ```tsx
 * // Simple fixed-width aside
 * <SplitPane asideWidth={400}>
 *   <SplitPane.Main>
 *     <DataTable />
 *   </SplitPane.Main>
 *   <SplitPane.Aside>
 *     <Inspector />
 *   </SplitPane.Aside>
 * </SplitPane>
 *
 * // Resizable with constraints
 * <SplitPane
 *   asideWidth={400}
 *   resizable
 *   minAsideWidth={280}
 *   minMainWidth={600}
 * >
 *   ...
 * </SplitPane>
 * ```
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type SplitPaneOrientation = "horizontal" | "vertical";

export interface SplitPaneProps {
  /** Width of aside panel (pixels or percentage string like "30%") */
  asideWidth?: number | string;
  /** Height of aside panel for vertical orientation (pixels or percentage) */
  asideHeight?: number | string;
  /** Minimum width of aside panel in pixels (only used if resizable=true) */
  minAsideWidth?: number;
  /** Minimum height of aside panel in pixels (only used if resizable=true and orientation="vertical") */
  minAsideHeight?: number;
  /** Minimum width of main panel in pixels (only used if resizable=true) */
  minMainWidth?: number;
  /** Minimum height of main panel in pixels (only used if resizable=true and orientation="vertical") */
  minMainHeight?: number;
  /** Orientation of the split */
  orientation?: SplitPaneOrientation;
  /** Whether the divider is draggable (default: false) */
  resizable?: boolean;
  /** Callback when resize occurs (provides new aside size in pixels) */
  onAsideResize?: (size: number) => void;
  /** Callback when resize starts */
  onResizeStart?: () => void;
  /** Callback when resize ends */
  onResizeEnd?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Additional CSS classes for the divider */
  dividerClassName?: string;
  /** Child panels */
  children?: React.ReactNode;
}

export interface SplitPanePanelProps {
  className?: string;
  children?: React.ReactNode;
}

// ——————————————————————————————————————————
// Context for sharing state
// ——————————————————————————————————————————

interface SplitPaneContextValue {
  orientation: SplitPaneOrientation;
  isResizing: boolean;
}

const SplitPaneContext = React.createContext<SplitPaneContextValue>({
  orientation: "horizontal",
  isResizing: false,
});

// ——————————————————————————————————————————
// Helper functions
// ——————————————————————————————————————————

function parseSize(size: number | string | undefined, defaultValue: number): number | string {
  if (size === undefined) return defaultValue;
  return size;
}

function getSizeInPixels(size: number | string, containerSize: number): number {
  if (typeof size === "number") return size;
  if (typeof size === "string" && size.endsWith("%")) {
    const percentage = Number.parseFloat(size);
    return (percentage / 100) * containerSize;
  }
  return Number.parseFloat(size.toString());
}

// ——————————————————————————————————————————
// Main Component
// ——————————————————————————————————————————

export function SplitPane({
  asideWidth,
  asideHeight,
  minAsideWidth = 240,
  minAsideHeight = 200,
  minMainWidth = 400,
  minMainHeight = 300,
  orientation = "horizontal",
  resizable = false,
  onAsideResize,
  onResizeStart,
  onResizeEnd,
  className,
  dividerClassName,
  children,
}: SplitPaneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  // Default aside size based on orientation
  const defaultAsideSize = orientation === "horizontal" ? 400 : 300;
  const asideSize =
    orientation === "horizontal" ? parseSize(asideWidth, defaultAsideSize) : parseSize(asideHeight, defaultAsideSize);

  // Track actual aside size in pixels during resize
  const [currentAsideSize, setCurrentAsideSize] = useState<number | null>(null);

  // Handle resize
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!containerRef.current || !isResizing) return;

      const rect = containerRef.current.getBoundingClientRect();
      const totalSize = orientation === "horizontal" ? rect.width : rect.height;
      const currentPos = orientation === "horizontal" ? e.clientX : e.clientY;

      // Calculate new aside size (from the right/bottom)
      const endPos = orientation === "horizontal" ? rect.right : rect.bottom;
      const newAsideSize = endPos - currentPos;
      const newMainSize = totalSize - newAsideSize;

      // Apply constraints
      const minAside = orientation === "horizontal" ? minAsideWidth : minAsideHeight;
      const minMain = orientation === "horizontal" ? minMainWidth : minMainHeight;

      if (newAsideSize < minAside || newMainSize < minMain) return;

      setCurrentAsideSize(newAsideSize);
      onAsideResize?.(newAsideSize);
    },
    [isResizing, orientation, minAsideWidth, minAsideHeight, minMainWidth, minMainHeight, onAsideResize]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    onResizeEnd?.();
  }, [onResizeEnd]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      onResizeStart?.();
    },
    [onResizeStart]
  );

  // Set up and tear down event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = orientation === "horizontal" ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp, orientation]);

  // Extract Main and Aside from children
  const mainPanel = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Main
  );
  const asidePanel = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Aside
  );

  // Calculate actual sizes to use
  const actualAsideSize = currentAsideSize ?? asideSize;
  const asideSizeStyle = typeof actualAsideSize === "number" ? `${actualAsideSize}px` : actualAsideSize;

  return (
    <SplitPaneContext.Provider value={{ orientation, isResizing }}>
      <div
        ref={containerRef}
        className={twMerge("flex w-full h-full", orientation === "horizontal" ? "flex-row" : "flex-col", className)}
      >
        {/* Main Panel */}
        <div className={twMerge("overflow-auto flex-1", orientation === "horizontal" ? "h-full" : "w-full")}>
          {mainPanel}
        </div>

        {/* Divider */}
        {resizable && (
          <div
            className={twMerge(
              "relative group flex-shrink-0",
              orientation === "horizontal" ? "w-2 -mx-1 cursor-col-resize" : "h-2 -my-1 cursor-row-resize",
              dividerClassName
            )}
            onMouseDown={handleMouseDown}
            role="separator"
            aria-orientation={orientation}
            aria-label="Resize divider"
            tabIndex={0}
            onKeyDown={(e) => {
              // Keyboard resize support
              if (!containerRef.current) return;

              const rect = containerRef.current.getBoundingClientRect();
              const totalSize = orientation === "horizontal" ? rect.width : rect.height;
              const currentSize = currentAsideSize ?? getSizeInPixels(asideSize, totalSize);
              const step = 20; // 20px step
              const minAside = orientation === "horizontal" ? minAsideWidth : minAsideHeight;
              const minMain = orientation === "horizontal" ? minMainWidth : minMainHeight;

              if (orientation === "horizontal") {
                if (e.key === "ArrowLeft") {
                  // Increase aside width
                  const newSize = Math.min(currentSize + step, totalSize - minMain);
                  setCurrentAsideSize(newSize);
                  onAsideResize?.(newSize);
                } else if (e.key === "ArrowRight") {
                  // Decrease aside width
                  const newSize = Math.max(currentSize - step, minAside);
                  setCurrentAsideSize(newSize);
                  onAsideResize?.(newSize);
                }
              } else {
                if (e.key === "ArrowUp") {
                  // Increase aside height
                  const newSize = Math.min(currentSize + step, totalSize - minMain);
                  setCurrentAsideSize(newSize);
                  onAsideResize?.(newSize);
                } else if (e.key === "ArrowDown") {
                  // Decrease aside height
                  const newSize = Math.max(currentSize - step, minAside);
                  setCurrentAsideSize(newSize);
                  onAsideResize?.(newSize);
                }
              }
            }}
          >
            {/* Visual divider line */}
            <div
              className={twMerge(
                "absolute bg-border-default transition-all",
                orientation === "horizontal"
                  ? "left-1/2 -translate-x-1/2 w-px h-full group-hover:w-1"
                  : "top-1/2 -translate-y-1/2 h-px w-full group-hover:h-1",
                "group-hover:bg-action-brand/50",
                isResizing && "bg-action-brand",
                isResizing && (orientation === "horizontal" ? "w-1" : "h-1")
              )}
            />
            {/* Grab handle indicator */}
            <div
              className={twMerge(
                "absolute",
                orientation === "horizontal"
                  ? "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-8"
                  : "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-1",
                "bg-border-muted rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              )}
            />
          </div>
        )}

        {/* Aside Panel */}
        <div
          className={twMerge("overflow-auto flex-shrink-0", orientation === "horizontal" ? "h-full" : "w-full")}
          style={{
            [orientation === "horizontal" ? "width" : "height"]: asideSizeStyle,
          }}
        >
          {asidePanel}
        </div>
      </div>
    </SplitPaneContext.Provider>
  );
}

// ——————————————————————————————————————————
// Panel Components
// ——————————————————————————————————————————

function Main({ className, children }: SplitPanePanelProps) {
  return <div className={twMerge("w-full h-full", className)}>{children}</div>;
}

function Aside({ className, children }: SplitPanePanelProps) {
  return <div className={twMerge("w-full h-full", className)}>{children}</div>;
}

// ——————————————————————————————————————————
// Compound API
// ——————————————————————————————————————————

SplitPane.Main = Main;
SplitPane.Aside = Aside;
